import type { Location } from '@adop/shared';

export interface LocationSelection<T> {
  /** Location used for display (falls back to the first available). */
  location: T | null;
  /** Id to persist because the user had not chosen yet, otherwise null. */
  persist: string | null;
  /** The stored id was set but is absent from this (possibly stale) list. */
  missing: boolean;
}

/**
 * Decides which location the workspace should display from the visible list and
 * the id persisted in localStorage.
 *
 * - No stored id: auto-select the first location and persist it.
 * - Stored id present: keep that exact location.
 * - Stored id missing (e.g. the cached `/me` response is stale): fall back for
 *   display but DO NOT overwrite the stored choice; signal `missing` so the
 *   caller can refresh auth data.
 */
export function resolveLocationSelection<T extends { id: string }>(
  locations: T[],
  storedId: string | null,
): LocationSelection<T> {
  const fallback = locations[0] ?? null;
  if (!storedId) {
    return { location: fallback, persist: fallback?.id ?? null, missing: false };
  }
  const stored = locations.find((entry) => entry.id === storedId) ?? null;
  if (stored) return { location: stored, persist: null, missing: false };
  return { location: fallback, persist: null, missing: true };
}

/** Archived locations stay manageable on the Locations page but are not switchable. */
export function isActiveLocation(location: Location): boolean {
  return !(location as { archived_at?: string | null }).archived_at;
}
